import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

/**
 * Lightweight hook returning the current user's unread notification count.
 * Subscribes to realtime changes and to the local 'notifications:read' event
 * so badges stay in sync after the user opens the notifications panel.
 */
export const useUnreadNotifications = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCount = async () => {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_read', false);
        if (error) throw error;
        if (!cancelled) setUnreadCount(count || 0);
      } catch (err) {
        console.error('Error fetching unread notifications count:', err);
        if (!cancelled) setUnreadCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCount();

    const handleLocalRead = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.all) {
        setUnreadCount(0);
      } else {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      fetchCount();
    };
    window.addEventListener('notifications:read', handleLocalRead);

    let debounce: NodeJS.Timeout;
    const debounced = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => fetchCount(), 200);
    };

    const channel = supabase
      .channel(`unread-notifications-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        debounced
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearTimeout(debounce);
      window.removeEventListener('notifications:read', handleLocalRead);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return { unreadCount, loading };
};
